import { useMemo } from 'react';
import {
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

// Static values between 40-110 for skeleton bars
const SKELETON_VALUES = [
  72, 95, 58, 83, 67, 104, 45, 89, 76, 52, 98, 61, 85, 70, 93, 48, 106, 64, 79,
  55, 91, 68, 100, 57,
];

/**
 * MachineHistoryGraphLoading - Loading state component for MachineHistoryGraph
 *
 * Displays a skeleton UI using Recharts that matches the actual graph appearance
 * with animated skeleton bars to prevent layout shift during data loading.
 *
 * Note: The header with TimeRangeSelector is rendered in the parent component
 * to prevent remounting and animation interruption.
 */
export function MachineHistoryGraphLoading() {
  // Generate skeleton data with static values
  const skeletonData = useMemo(
    () =>
      SKELETON_VALUES.map((value, i) => ({
        time: i,
        value,
      })),
    []
  );

  return (
    <>
      {/* Graph placeholder with skeleton bars */}
      <div className='flex h-52.5 flex-col gap-2.5'>
        <div className='flex items-start gap-3.5'>
          {/* Y-axis Labels skeleton */}
          <div
            className='size-caption flex h-40 flex-col items-end justify-between
              pt-1'
          >
            {[150, 120, 90, 60, 30, 0].map((val) => (
              <span
                key={val}
                className='h-3 w-8 animate-pulse rounded bg-gray-200'
              />
            ))}
          </div>

          {/* Recharts skeleton */}
          <div className='flex-1 animate-pulse'>
            <ResponsiveContainer width='100%' height={160}>
              <BarChart
                data={skeletonData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                barGap={0}
                barCategoryGap={0}
              >
                <CartesianGrid
                  strokeDasharray='0'
                  stroke='#E5E7EB'
                  vertical={false}
                />
                <XAxis hide />
                <YAxis hide domain={[0, 150]} />
                <ReferenceLine
                  y={90}
                  stroke='#FECACA'
                  strokeDasharray='5 5'
                  strokeWidth={2}
                  opacity={0.75}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* X-axis Labels skeleton */}
        <div className='size-caption flex items-center justify-between pl-10.75'>
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className='h-3 w-10 animate-pulse rounded bg-gray-200'
            />
          ))}
        </div>
      </div>

      {/* Navigation skeleton */}
      <div className='flex items-center justify-center gap-4 px-20 pt-3 pb-2'>
        <div className='h-6 w-6 animate-pulse rounded bg-gray-200' />
        <div className='h-4 w-32 animate-pulse rounded bg-gray-200' />
        <div className='h-6 w-6 animate-pulse rounded bg-gray-200' />
      </div>
    </>
  );
}
